import axios from 'axios';
import { useEffect, useState } from 'react';
import { useAppDispatch } from '../app/hooks';
import { Benefits } from '../components/BusinessDashboard';
import AboutBusiness from '../components/BusinessDashboard/BusinessRegistrationWrapper/AboutBusiness';
import BusinessAddress from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessAddress';
import BusinessDocumentUpload from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessDocumentUpload';
import BusinessFinalScreen from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessFinalScreen';
import BusinessName from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessName';
import BusinessOwnerShipInformation from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessOwnerShipInformation';
import BusinessResidentAddress from '../components/BusinessDashboard/BusinessRegistrationWrapper/BusinessResidentAddress';
import SuccessModal from '../components/global/SuccessModal';
import Wrapper from '../components/global/Wrapper';
import { setAllPackages } from '../features/packages/packagesSlice';
import { useCreateNewEnterpreneur } from '../hooks/useCreateNewEnterpreneur';
import { TInfo, TStep3Branch, TStep3Info, TStep4Info, TStep5Info, TStep6Info } from '../types';
import { baseUrl_, timeout } from '../utils/constants';
import { decryptJson } from '../utils/decryptJson';
import { fetchPackages } from '../utils/getPackageName';
import { getFromLocalStorage, setInLocalStorage } from '../utils/setRegInfoInLocalStorage';
import useUpdateSearchParams from '../hooks/useUpdateSearchParams';
import { Request } from '../apis/request';

const getFormValues = (form: string, noOfPartners?: number) => {
  const storedValues = getFromLocalStorage(form);

  if (
    !storedValues &&
    !(form === 'branches' || form === 'partners' || form === 'form6' || form === 'form5')
  ) {
    return {} as TInfo | TStep3Branch | TStep3Info | TStep4Info;
  } else if (!storedValues && (form === 'branches' || form === 'partners')) {
    return [];
  } else if (!storedValues && (form === 'form5' || form === 'form6')) {
    const arr = Array.from(Array(noOfPartners).keys());
    const partArr = arr.map(() => ({}));
    const init = [{}].concat(partArr);
    return init;
  }

  const parsedStoreValue = storedValues && JSON.parse(storedValues);
  return parsedStoreValue;
};

const BusinessRegistration = () => {
  const [step, setStep] = useState(1);
  const [step1, setStep1] = useState(getFormValues('form1'));
  const { update } = useUpdateSearchParams();

  const [step3Info, setStep3Info] = useState<TStep3Info>(getFormValues('form3'));
  const [step3Branches, setStep3Branches] = useState<TStep3Branch[]>(getFormValues('branches'));

  const [step4Info, setStep4Info] = useState<TStep4Info>(getFormValues('form4'));
  const [step4Partners, setStep4Partners] = useState<TStep4Info[]>(getFormValues('partners'));

  // AN ALGORITHM FOR STEP 5 //
  const step4PartnersName = step4Partners?.map(
    (partner) => partner.firstName + ' ' + partner.lastName,
  );
  const ownersName = step4Info.firstName + ' ' + step4Info.lastName;
  const [step5Info, setStep5Info] = useState<TStep5Info[]>(
    getFormValues('form5', step4Partners.length),
  );

  const [step6Info, setStep6Info] = useState<TStep6Info[]>(
    getFormValues('form6', step4Partners.length),
  );
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const arr = Array.from(Array(step4Partners.length).keys());
    const partArr = arr.map(() => ({} as TStep5Info));
    const partArr2 = arr.map(() => ({} as TStep6Info));
    const init = [{} as TStep5Info].concat(partArr);
    setStep5Info(init);
    const init2 = [{} as TStep6Info].concat(partArr2);
    setStep6Info(init2);
  }, [step4Partners]);

  useEffect(() => {
    setInLocalStorage({ prop: 'form1', value: step1 });
    if (step1.structureOfBusiness === 'Sole proprietorship(One-Man)') {
      localStorage.removeItem('partners');
      setStep4Partners([]);
    }
  }, [step1]);

  useEffect(() => {
    setInLocalStorage({ prop: 'form3', value: step3Info });
  }, [step3Info]);

  useEffect(() => {
    setInLocalStorage({ prop: 'branches', value: step3Branches });
  }, [step3Branches]);

  useEffect(() => {
    setInLocalStorage({ prop: 'form4', value: step4Info });
  }, [step4Info]);
  useEffect(() => {
    setInLocalStorage({ prop: 'partners', value: step4Partners });
  }, [step4Partners]);

  useEffect(() => {
    setInLocalStorage({ prop: 'form5', value: step5Info });
  }, [step5Info]);

  useEffect(() => {
    setInLocalStorage({ prop: 'form6', value: step6Info });
  }, [step6Info]);
  useEffect(() => {
    update({ param: 'step', value: step });
  }, [step]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    Request.get(`/servicechargelogs/active`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('login-token')}`,
        sessId: localStorage.getItem('sessId') ?? '',
      },
      timeout: timeout,
    }).then((data) => {
      const res = decryptJson(data?.data?.data);
      dispatch(setAllPackages(res));
    });
  }, []);

  const { callCreateEntrepreneurMutation, loading } = useCreateNewEnterpreneur({
    step1Info: step1,
    step3Branches,
    step3Info,
    step4Info,
    step5Info,
    step6Info,
    step4Partners,
    setShowModal,
  });

  const handleFinalSubmit = () => {
    callCreateEntrepreneurMutation();
  };

  const benefits = [
    'Refrain from using popular names, names of states, cities and country',
    'Avoid hard to spell names. Keep it simple.',
    "Don't pick a name that could be limiting as your business grows.",
    'Conduct a thorough Internet search on the name to be sure no other person is using it.',
    'Assess and get feedback on your name. Is it catchy?',
    'Avoid using popular names, they most likely would have been taken.',
    'Bear in mind that you will still need to brand your name, so keep it simple, catchy and relevant.',
  ];
  const PageDisplay = () => {
    if (step === 1) {
      return (
        <AboutBusiness
          step1={step1}
          setStep1={setStep1}
          setStep={setStep}
          setPartners={setStep4Partners}
          step={step}
        />
      );
    } else if (step === 2) {
      return <BusinessName setStep={setStep} setStep1={setStep1} step1={step1} step={step} />;
    } else if (step === 3) {
      return (
        <BusinessAddress
          step3Info={step3Info}
          setStep={setStep}
          setStep3Info={setStep3Info}
          branches={step3Branches}
          setStep3Branches={setStep3Branches}
          step={step}
        />
      );
    } else if (step === 4) {
      return (
        <BusinessOwnerShipInformation
          step4Info={step4Info}
          setStep={setStep}
          setStep4Info={setStep4Info}
          partners={step4Partners}
          setPartners={setStep4Partners}
          // step={step}
        />
      );
    } else if (step === 5) {
      return (
        <BusinessResidentAddress
          setStep={setStep}
          step4Partners_={step4Partners}
          step4Info={step4Info}
          step5Info={step5Info}
          setStep5Info={setStep5Info}
          ownersName={ownersName}
          partnersName={step4PartnersName}
          // step={step}
        />
      );
    } else if (step === 6) {
      return (
        <BusinessDocumentUpload
          setStep={setStep}
          step4Partners_={step4Partners}
          setStep6Info={setStep6Info}
          step6Info={step6Info}
          step4Info={step4Info}
          // step={step}
        />
      );
    } else if (step === 7) {
      return (
        <BusinessFinalScreen
          step1Info={step1}
          step3Info={step3Info}
          step4Info={step4Info}
          partners={step4Partners}
          step6Info={step6Info}
          handleSubmit={handleFinalSubmit}
          setStep={setStep}
          loading={loading}
          // step={step}
        />
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const topText: any = {
    1: `Tell us more about your business`,
    2: `Propose 3 chosen business names`,
    3: `Provide us your business address`,
    4: `Provide Primary Owner's Personal details`,
    5: `Provide us your residential address`,
    6: `Upload necessary documents`,
  };

  return (
    <Wrapper className='h-[100%] space-y-3 pt-[12px] md:pt-0 md:space-y-8  !mr-0'>
      <div className='md:block lg:hidden xl:flex m-0'>
        {step === 6 && (
          <Benefits
            benefits={[
              'You must ensure that the address shown in this setion is easily traceable.',
            ]}
            label='Address Hint'
          />
        )}
        {step === 3 && (
          <Benefits
            benefits={[
              'You must ensure that the address shown in this setion is easily traceable.',
            ]}
            label='Address Hint'
          />
        )}
        {step === 2 && (
          <Benefits benefits={benefits} label='How to get business names approved by CAC' />
        )}
        {/* {step === 'preview-form' && (
          <Benefits benefits={benefits} label='How to get business names approved by CAC' />
        )} */}
      </div>

      <div className=' items-center my-[102px] mb-10 '>
        <header
          className={`flex justify-center pt-[40px]  md:pt-[80px] w-full lg:w-[700px] mx-auto`}
        >
          <div className='w-full'>
            <div className='text-[12px] font-poppins font-medium leading-[15px] mb-[6px] text-black '>
              STEP {step} of 7
            </div>
            <div className='bg-[#F8B40042] rounded-[1px] inline-block pt-1 pl-2 pr-1 pb-1 mb-[5px] text-[9.40px] font-normal font-poppins leading-[14px] '>
              {step === 1
                ? 'Nature of business'
                : step === 2
                ? 'Business Names'
                : step === 3
                ? 'Business Address'
                : step === 4
                ? 'Ownership Information'
                : step === 5
                ? 'Residential Address'
                : step === 6 && 'Upload Documents'}
            </div>
            <h2 className='text-[28px] leading-[42px] font-medium font-poppins  '>
              Lets get your business registered!
            </h2>
            <hr className='h-[1px] my-[18px] w-[100%] bg-[#CDD5D5]' />
            {/* <div className='border-b-[1px] my-[18px] w-[348px] border-[#CDD5D5]'></div> */}
            <h2
              className='mb-[27px] inline-block p-[5px] rounded-sm text-[#005555]'
              style={{ backgroundColor: 'rgba(4, 158, 38, 0.13)' }}
            >
              {topText[step]}
            </h2>
          </div>
        </header>
        {PageDisplay()}
      </div>

      <SuccessModal
        showSuccessModal={showModal}
        setShowSuccessModal={setShowModal}
        pageComingFrom={'Login'}
        description={`Your business name registration form has been submitted and received`}
        proceedText={`PROCEED TO PAYMENT`}
        //whereTo={`/payment/payment-link`}
        // img={successLoader}
        // closePreviousModal={() => setTokenSent(false)}
      />
    </Wrapper>
  );
};

export default BusinessRegistration;
